import { FC, useState } from 'react';
import {
  GenericGridItem,
  WbCard,
  WbCardActions,
  WbButton,
  WbCardContent,
  WbDrawer,
  WbDrawerContent,
  WbGenericFieldGrid,
  WbTagsArray,
  WbTooltip,
} from '@agilelab/plugin-wb-platform';
import { configApiRef, useApi } from '@mainstage/core-plugin-api';
import { Box, makeStyles, Typography } from '@material-ui/core';
import { format, parseISO } from 'date-fns';
import { omit } from 'lodash';
import { useNavigate } from 'react-router';
import { useReleaseDetailPageContext } from './context/useReleaseDetailPageContext';
import { DeploymentHistoryCard } from './DeploymentHystoryCard/DeploymentHystoryCard';
import { ReleaseDeploymentStatus } from './ReleaseDeploymentStatus';

const useStyles = makeStyles(theme => ({
  container: {
    display: 'flex',
    flexDirection: 'row',
    overflow: 'auto',
    [theme.breakpoints.down('sm')]: {
      flexDirection: 'column',
    },
  },
  rightSection: {
    paddingLeft: '16px',
    borderLeft: `1px solid ${theme.palette.grey[200]}`,
    padding: '16px 16px',
    rowGap: '0px',
    minWidth: '20%',
    display: 'grid',
    [theme.breakpoints.down('sm')]: {
      borderTop: `1px solid ${theme.palette.grey[200]}`,
      paddingLeft: '0px',
      borderLeft: 'none',
      minWidth: '100%',
    },
  },
}));

export const AboutCard: FC = () => {
  const classes = useStyles();
  const [isDeploymentHistoryDrawerOpen, setIsDeploymentHistoryDrawerOpen] =
    useState(false);
  const { release, marketplaceLink, deploymentUnitStatusState } =
    useReleaseDetailPageContext();
  const navigate = useNavigate();
  const config = useApi(configApiRef);

  const marketplaceEnabled =
    config.getOptionalBoolean('mesh.marketplace.enabled') ?? true;

  const repoLink =
    release.metadata.annotations?.['backstage.io/source-location'].slice(4) ||
    release.metadata.repoUrl;
  let provisioningInfo =
    deploymentUnitStatusState.value?.provisioningDetails?.provisioningInfo
      ?.privateInfo;

  if (!marketplaceEnabled || !marketplaceLink) {
    provisioningInfo = omit(provisioningInfo, ['marketplaceInfo']);
  }

  const tags = release.metadata.tags?.map(tag => ({ tagFQN: tag })) ?? [];

  const section1Fields: GenericGridItem[] = [
    {
      label: 'Name',
      value: release.metadata.name,
      showTooltip: true,
      colSpan: { xs: 4, md: 2 },
    },
    {
      label: 'Created At',
      value: format(
        parseISO(
          release.metadata.isSnapshot && release.metadata.committedAt
            ? release.metadata.committedAt
            : release.metadata.createdAt,
        ),
        'yyyy/MM/dd HH:mm:ss',
      ),
      colSpan: { xs: 4, md: 2 },
    },
    ...(release.metadata.releasedAt
      ? [
          {
            label: 'Released At',
            value: format(
              parseISO(release.metadata.releasedAt),
              'yyyy/MM/dd HH:mm:ss',
            ),
            colSpan: { xs: 4, md: 2 },
          },
        ]
      : []),
  ];

  const section2Fields: GenericGridItem[] = [
    {
      label: 'Repository Link',
      value: repoLink,
      href: repoLink,
      showTooltip: true,
      colSpan: { xs: 4, md: 2 },
    },
    ...Object.values(provisioningInfo ?? {}).map(info => ({
      label: info.label ?? '-',
      href: info.href,
      value: info.value ?? info.href,
      type: (info.type ?? 'string') as 'string' | 'text' | 'date',
      colSpan: { xs: 4, md: 2 },
    })),
  ];

  const section3Fields: GenericGridItem[] = [
    {
      label: 'Tags',
      value: tags.length ? (
        <WbTagsArray tags={tags} />
      ) : (
        <Typography>No tags</Typography>
      ),
      colSpan: 4,
    },
  ];

  return (
    <>
      <WbCard
        title="About"
        actions={
          <WbCardActions breakpoint="sm">
            <WbButton
              label="Deployment History"
              variant="outlined"
              onClick={() => setIsDeploymentHistoryDrawerOpen(true)}
            />
            {marketplaceEnabled && marketplaceLink ? (
              <WbTooltip title={marketplaceLink}>
                <WbButton
                  label="View in Marketplace"
                  variant="outlined"
                  onClick={() => {
                    if (marketplaceLink.startsWith('http')) {
                      window.location.href = marketplaceLink;
                      return;
                    }
                    navigate(marketplaceLink);
                  }}
                />
              </WbTooltip>
            ) : null}
          </WbCardActions>
        }
      >
        <WbCardContent>
          <Box className={classes.container}>
            <Box display="flex" flexDirection="column" flex="1">
              <WbGenericFieldGrid
                fields={[
                  ...section1Fields,
                  ...section2Fields,
                  ...section3Fields,
                ]}
                columns={4}
                borderBetweenRows
                borderAfterLastRow={false}
              />
            </Box>

            <Box className={classes.rightSection}>
              <ReleaseDeploymentStatus />
            </Box>
          </Box>
        </WbCardContent>
      </WbCard>

      <WbDrawer
        open={isDeploymentHistoryDrawerOpen}
        setOpen={setIsDeploymentHistoryDrawerOpen}
        size={90}
        title="Deployment History"
        ModalProps={{ onClose: () => setIsDeploymentHistoryDrawerOpen(false) }}
      >
        <WbDrawerContent>
          <DeploymentHistoryCard />
        </WbDrawerContent>
      </WbDrawer>
    </>
  );
};
